import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
} from "@mui/material";
import SelectField from "../../atoms/SelectField/SelectField";
import { findAllModules } from "../../../API/apiModules";
import {
  createPage,
  findPage,
  updatePage,
} from "../../../API/apiPages";
import { SystemModule } from "../../../types/SystemModule";
import styles from "./GestaoPaginas.module.css";
import styles_grid from "../../../styles/Grid.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";

const NovaPagina = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("id");
  const isEdit = Boolean(pageId);

  const [modules, setModules] = useState<SystemModule[]>([]);
  const [moduleName, setModuleName] = useState("");
  const [name, setName] = useState("");
  const [route, setRoute] = useState("");
  const [description, setDescription] = useState("");
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);

  const selectedModule = useMemo(
    () => modules.find((module) => module.name === moduleName),
    [moduleName, modules],
  );

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    if (!isEdit || !pageId) return;
    loadPage(pageId);
  }, [isEdit, pageId, modules.length]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const loadModules = async () => {
    setLoadingModules(true);
    try {
      const response = await findAllModules({ active: true });
      setModules(response.data || []);
    } catch (error) {
      setMsgError("Não foi possível carregar os módulos.");
    } finally {
      setLoadingModules(false);
    }
  };

  const loadPage = async (id: string) => {
    setLoadingPage(true);
    try {
      const response = await findPage(id);
      const currentModuleName =
        response.module?.name ||
        modules.find((module) => module.id === response.module_id)?.name ||
        "";

      setModuleName(currentModuleName);
      setName(response.name || "");
      setRoute(response.route || "");
      setDescription(response.description || "");
    } catch (error) {
      setMsgError("Não foi possível carregar a página.");
    } finally {
      setLoadingPage(false);
    }
  };

  const handleSave = async () => {
    if (!selectedModule || !name.trim() || !route.trim() || !description.trim()) {
      setMsgError("Preencha módulo, nome, rota e descrição.");
      return;
    }

    try {
      setLoadingSave(true);
      const payload = {
        module_id: selectedModule.id,
        name: name.trim(),
        route: route.trim(),
        description: description.trim(),
      };

      if (isEdit && pageId) {
        await updatePage(pageId, payload);
        setMsgSuccess("Página atualizada com sucesso.");
      } else {
        await createPage(payload);
        setMsgSuccess("Página criada com sucesso.");
        setModuleName("");
        setName("");
        setRoute("");
        setDescription("");
      }

      setTimeout(() => navigate("/paginas"), 700);
    } catch (error) {
      setMsgError("Não foi possível salvar a página.");
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>
          {isEdit ? "Editar Página" : "Nova Página"}
        </div>

        {msgSuccess && (
          <div className={styles_general.mt4}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={styles_general.mt4}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        {loadingPage || loadingModules ? (
          <div className={styles_general.mt6}>
            <CircularProgress />
          </div>
        ) : (
          <div className={`${styles_grid.grid12} ${styles_general.mt6}`}>
            <div className={styles_grid.inputGroup4}>
              <label className={styles_input.titleInput}>Módulo</label>
              <SelectField
                label=""
                value={moduleName}
                onChange={setModuleName}
                options={modules.map((module) => ({ nome: module.name }))}
                disableTyping
                disableClearable
              />
            </div>

            <div className={styles_grid.inputGroup4}>
              <label className={styles_input.titleInput}>Nome</label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 150 }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={styles_grid.inputGroup4}>
              <label className={styles_input.titleInput}>Rota</label>
              <TextField
                size="small"
                placeholder="/minha-rota"
                variant="standard"
                fullWidth
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                inputProps={{ maxLength: 150 }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={styles_grid.inputGroupFull}>
              <label className={styles_input.titleInput}>Descrição</label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 255 }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={`${styles_grid.inputGroupFull} ${styles_grid.justifyEnd} ${styles_general.mt6}`}>
              <Button
                variant="contained"
                onClick={() => navigate("/paginas")}
                className={styles_button.buttonSquareSecondary}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                className={`${styles_button.buttonSquarePrimary} ${loadingSave ? styles_button.buttonOpacity : ""}`}
                disabled={loadingSave}
              >
                {loadingSave ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NovaPagina;
